import { SALE_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { ETH_SC_ADDRESS, NODE_SALE_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { ETH_DECIMAL, USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import { TOKEN_CONFIG } from '@/global/constants/payment.constant';
import { CoinSymbol } from '@/global/enums/common.enums';
import type { SignDealPriceSchemaType, SignPriceSchemaType } from '@/global/schemas/price.schema';
import { useGA, useWeb3 } from '@/hooks';
import { useFetchPricesOTCV2Mutation, useRequestPriceV2Mutation } from '@/store/services/price.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import type {
  ApproveCallbackParams,
  BuyCallbackParams,
  BuyNodeV1Hook,
  BuyOTCCallbackParams,
} from '../types/buyNode.type';
import { RedeemOptions } from '../types/redeem.type';

const COIN_APPROVE_MAX = [CoinSymbol.USDC, CoinSymbol.USDT];
const MAXIMUM_APPROVE_AMOUNT = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff';

const useBuyNodeV2 = (): BuyNodeV1Hook => {
  const { value: isExecuting, setTrue: setIsExecutingTrue, setFalse: setIsExecutingFalse } = useBoolean(false);
  const { address, config } = useWeb3();
  const { trackButtonClickEvent, trackGAEvent } = useGA();
  const [requestPriceV2] = useRequestPriceV2Mutation();
  const [fetchPricesOtcV2] = useFetchPricesOTCV2Mutation();

  const { nodeId, referralCode, nodeVersion } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const spender = nodeVersion === 'v2' && projectInfo?.sale ? projectInfo?.sale : NODE_SALE_V1_SC_ADDRESS;

  const handleBuyTransaction = async (
    currency: CoinSymbol,
    usdAmount: number,
    signatureData: SignPriceSchemaType,
    count: number,
    nft: string,
  ) => {
    if (!address || !projectInfo?.sale) return;
    try {
      const {
        discount,
        price,
        decimals,
        deadline,
        nonce,
        signature: { r, s, v },
      } = signatureData;

      const baseArgs = {
        amount: count,
        token: currency === CoinSymbol.BSC ? ETH_SC_ADDRESS : TOKEN_CONFIG[currency].address,
        price,
        priceDecimals: decimals,
        discountPercent: discount,
        buyer: address,
        nonce,
        deadline,
        v,
        r,
        s,
      };
      const discountPercent = (100 - (discount ?? 0) / 100) / 100;

      const value =
        currency === CoinSymbol.BSC
          ? BigInt(Math.ceil(((usdAmount * 10 ** (ETH_DECIMAL + decimals) * 1.03) / price) * discountPercent))
          : BigInt(0);

      return await writeContract(config, {
        abi: SALE_ABI,
        address: projectInfo.sale as `0x${string}`,
        functionName: `buy`,
        args: [Math.ceil(usdAmount * 1.008 * 10 ** USD_VALUE_DECIMAL), baseArgs],
        value,
      });
    } catch (error: unknown) {
      const errorDetails = logError(error, `handleBuyTransaction-${currency}`);
      const errorMessage =
        errorDetails.message || (error instanceof Error ? error.message : `Buy transaction failed for ${currency}`);

      // Re-throw the error with more context
      throw new Error(`Transaction failed: ${errorMessage}`, { cause: error });
    }
  };

  const handleBuyOTCTransaction = async (currency: CoinSymbol, signatureData: SignDealPriceSchemaType) => {
    if (!address || !projectInfo?.sale) return;
    try {
      const {
        amount,
        tokenAmount,
        fromTime,
        toTime,
        nonce,
        receivers,
        percents,
        supply,
        signature: { r, s, v },
      } = signatureData;

      const option =
        supply === 'CUT_FROM_HIGHEST_TIER'
          ? RedeemOptions.CUT_FROM_HIGHEST_TIER
          : supply === 'CUT_FROM_CURRENT_TIER'
            ? RedeemOptions.CUT_FROM_CURRENT_TIER
            : RedeemOptions.ONLY_MINT;

      const baseArgs = {
        option,
        amount,
        token: currency === CoinSymbol.BSC ? ETH_SC_ADDRESS : TOKEN_CONFIG[currency].address,
        tokenAmount,
        buyer: address,
        nonce,
        fromTime,
        toTime,
        receivers,
        percents,
        v,
        r,
        s,
      };
      const value = currency === CoinSymbol.BSC ? BigInt(tokenAmount) : BigInt(0);

      return await writeContract(config, {
        abi: SALE_ABI,
        address: projectInfo.sale as `0x${string}`,
        functionName: `otc`,
        args: [baseArgs],
        value,
      });
    } catch (error: unknown) {
      const errorDetails = logError(error, `handleBuyTransaction-${currency}`);
      const errorMessage =
        errorDetails.message || (error instanceof Error ? error.message : `Buy transaction failed for ${currency}`);

      // Re-throw the error with more context
      throw new Error(`Transaction failed: ${errorMessage}`, { cause: error });
    }
  };

  const handleBuyCreditCard = async ({
    amount,
    token,
    isCrossmint,
    receivedAddress,
  }: BuyCallbackParams & { token: CoinSymbol }) => {
    try {
      const signatureRes = await requestPriceV2({
        amount,
        buyerAddress: receivedAddress,
        coin: TOKEN_CONFIG[token].key,
        sale: nodeId,
        nft: nodeId,
        referralCode: referralCode ? referralCode : undefined,
        isCrossmint,
      });
      if (
        !signatureRes.data ||
        signatureRes.data?.remain <= 0 ||
        (signatureRes.data?.remain && signatureRes.data?.remain < amount)
      ) {
        toast.error('You amount is over exceed limit of project allow each address to buy');
        return;
      }
      if (!signatureRes.data.signature) {
        toast.error('Signature is invalid. Please try again!');
        return;
      }
      return signatureRes;
    } catch (error) {
      const errorDetails = logError(error, `handleBuy-${token}`);
      const errorMessage =
        errorDetails.message || (error instanceof Error ? error.message : 'Buy Node Sale failed, please try again');
      toast.error(errorMessage);
    }
  };

  const handleBuy = useCallback(
    async ({ usdAmount, amount, token, onSuccess, onError }: BuyCallbackParams & { token: CoinSymbol }) => {
      if (!nodeId || !address) return;
      setIsExecutingTrue();
      trackButtonClickEvent({ name: 'buy', address });

      try {
        const signatureRes = await requestPriceV2({
          amount,
          buyerAddress: address,
          coin: TOKEN_CONFIG[token].key,
          sale: nodeId,
          nft: nodeId,
          ...(referralCode !== '' && { referralCode }),
        });

        if (
          !signatureRes.data ||
          signatureRes.data?.remain <= 0 ||
          (signatureRes.data?.remain && signatureRes.data?.remain < amount)
        ) {
          toast.error('You amount is over exceed limit of project allow each address to buy');
          return;
        }

        if (!signatureRes.data.signature) {
          toast.error('Signature is invalid. Please try again!');
          return;
        }

        const hash = await handleBuyTransaction(token, usdAmount, signatureRes.data, amount, nodeId);

        if (!hash) {
          toast.error('Buy Node Sale failed, please try again');
          return;
        }

        toast(TransactionSubmittedToast());
        const receipt = await waitForTransactionReceipt(config, { hash });
        trackGAEvent({
          action: 'purchase',
          category: projectInfo?.node?.name,
          label: 'purchase success',
          value: usdAmount,
          transaction_id: receipt.transactionHash,
          fields: {
            transaction_id: receipt.transactionHash,
            method: token,
            buyer: address,
            currency: 'usd',
            payment_type: token,
            items: [
              {
                item_id: projectInfo?.nft,
                item_name: projectInfo?.node?.name,
                item_category: projectInfo?.node?.name,
                quantity: amount,
                price: usdAmount / amount,
              },
            ],
          },
        });
        onSuccess?.(receipt.transactionHash);
      } catch (error: unknown) {
        const errorDetails = logError(error, `handleBuy-${token}`);
        const errorMessage =
          errorDetails.message || (error instanceof Error ? error.message : 'Buy Node Sale failed, please try again');

        toast.error(errorMessage);
        onError?.(errorMessage);
        throw error;
      } finally {
        setIsExecutingFalse();
      }
    },
    [
      config,
      address,
      nodeId,
      projectInfo,
      referralCode,
      requestPriceV2,
      setIsExecutingTrue,
      setIsExecutingFalse,
      trackGAEvent,
      trackButtonClickEvent,
    ],
  );

  const handleBuyOTC = useCallback(
    async ({ otcId, amount = 1, token, onSuccess, onError }: BuyOTCCallbackParams & { token: CoinSymbol }) => {
      if (!address || !nodeId) return;
      setIsExecutingTrue();
      trackButtonClickEvent({ name: 'otc', address });

      try {
        const signatureRes = await fetchPricesOtcV2({
          otcId,
          amount,
          buyerAddress: address,
          coin: TOKEN_CONFIG[token].key,
          nft: nodeId,
        });

        if (!signatureRes.data || !signatureRes.data.signature) {
          toast.error('Signature is invalid. Please try again!');
          return;
        }

        const hash = await handleBuyOTCTransaction(token, signatureRes.data);

        if (!hash) {
          toast.error('Buy Node Sale failed, please try again');
          return;
        }

        toast(TransactionSubmittedToast());
        const receipt = await waitForTransactionReceipt(config, { hash });
        trackGAEvent({
          action: 'purchase',
          category: projectInfo?.node?.name,
          label: 'purchase otc success',
          value: 0,
          transaction_id: receipt.transactionHash,
          fields: {
            transaction_id: receipt.transactionHash,
            method: token,
            buyer: address,
            currency: 'usd',
            payment_type: token,
            items: [
              {
                item_id: projectInfo?.nft,
                item_name: projectInfo?.node?.name,
                item_category: projectInfo?.node?.name,
                quantity: amount,
                price: 0,
              },
            ],
          },
        });
        return onSuccess?.(receipt.transactionHash);
      } catch (error) {
        const errorDetails = logError(error, 'handleBuyOTC');
        const errorMessage =
          errorDetails.message || (error instanceof Error ? error.message : 'Buy OTC failed, please try again');
        toast.error(errorMessage);
        onError?.(errorMessage);
        throw error;
      } finally {
        setIsExecutingFalse();
      }
    },
    [address, nodeId, config, fetchPricesOtcV2, setIsExecutingTrue, setIsExecutingFalse, trackButtonClickEvent],
  );

  const handleApprove = useCallback(
    async (
      currency: CoinSymbol,
      { priceInUsd, tokenPrice, onSuccess, onError }: ApproveCallbackParams,
      isOTC?: boolean,
    ) => {
      if (currency === CoinSymbol.BSC) {
        onSuccess?.();
        return;
      }

      const tokenConfig = TOKEN_CONFIG[currency];
      try {
        setIsExecutingTrue();
        trackButtonClickEvent({ name: 'approve', address });
        const amount =
          COIN_APPROVE_MAX.includes(currency) && priceInUsd !== 0
            ? MAXIMUM_APPROVE_AMOUNT
            : isOTC
              ? Math.ceil(priceInUsd * 1.008) * 10 ** tokenConfig.decimals
              : Math.ceil(Number(((priceInUsd * 1.008) / tokenPrice) * 1.1)) * 10 ** tokenConfig.decimals;

        const hash = await writeContract(config, {
          abi: tokenConfig.abi,
          address: tokenConfig.address as `0x${string}`,
          functionName: 'approve',
          args: [spender, amount],
          value: BigInt(0),
        });
        await waitForTransactionReceipt(config, { hash });
        toast.success(`Approve ${currency} successfully`);
        onSuccess?.();
      } catch (error: unknown) {
        const errorDetails = logError(error, `handleApprove-${currency}`);
        const errorMessage =
          errorDetails.message || (error instanceof Error ? error.message : `Approve ${currency} failed`);
        onError?.();
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsExecutingFalse();
      }
    },
    [config, setIsExecutingTrue, setIsExecutingFalse, trackButtonClickEvent, spender],
  );

  return {
    handleBuy,
    handleBuyOTC,
    handleApprove,
    isExecuting,
    handleBuyCreditCard,
  };
};

export { useBuyNodeV2 };
